/**
 * Reading the documents people forward, on this machine, with a converter this site serves itself.
 *
 * A PDF, a deck, a spreadsheet: the words are inside the file, so a recogniser pointed at a picture
 * of the page is the wrong tool, and the obvious right one for PDFs alone (pdf.js) is 34 MB. anydoc
 * is the trade that works: one 6.7 MB WebAssembly module (2.9 MB over the wire, gzipped) that turns
 * any of a dozen formats into Markdown without a model, a worker, or a service.
 *
 * The reason it can sit behind this site's promise at all is what it *cannot* do. anydoc's CLI and
 * Node builds can send a scanned document to a hosted OCR service; the WebAssembly build has no
 * such path — it throws `needsOcr` and stops. So `connect-src 'self'` still holds, and the only
 * fetch reading a document makes is for the module itself, from this origin, the first time someone
 * attaches one. Same shape as `ocr.ts`, and for the same reason: see that file for the full
 * argument against a CDN.
 *
 * What that leaves unread, stated plainly rather than hidden: a PDF that is a photograph of a
 * page. Its words are in an image, and pulling them out would mean rasterising the pages first,
 * which is the 34 MB we just declined. The funnel says so, and says what to do instead.
 */

/** What the converter is asked for: the file's bytes as Markdown. */
interface Engine {
  toMarkdownBytes: (bytes: Uint8Array, format?: string | null) => string;
}

/** How the converter is fetched. Injectable so the path and the degradation are testable. */
export type LoadConverter = () => Promise<Engine>;

/** Long enough for a long itinerary on a slow machine, short enough not to hang the funnel. */
const TIMEOUT_MS = 60_000;

const loadAnydoc: LoadConverter = async () => {
  const anydoc = await import('@firecrawl/anydoc-wasm');
  // No path passed, deliberately: the module's own default is `new URL('…_bg.wasm',
  // import.meta.url)`, which the build rewrites to the hashed asset it emitted from node_modules.
  // That is already same-origin — unlike Tesseract, whose defaults point at a CDN and have to be
  // overridden one at a time — so the promise holds without a second copy of a 6.7 MB file.
  await anydoc.default();
  return anydoc as Engine;
};

/**
 * What the converter is handed, by extension, and the format name it is told to expect.
 *
 * anydoc can name the format from the bytes, and its own `formatFromExtension` knows this table
 * already — but asking it would mean downloading 6.7 MB to decide whether to download 6.7 MB. So
 * the routing table is here, small and explicit, and the module is only fetched for a file it says
 * it can read.
 *
 * `docx`, `rtf`, `html`, `eml` and plain text are *not* here on purpose: `documentTextFrom` in
 * `@project/core` already reads those, in a few KB, in the app as well as the site. `doc` is —
 * the legacy binary format is the one Word file that has never been readable here.
 */
const CONVERTIBLE: Record<string, string> = {
  pdf: 'pdf',
  doc: 'doc',
  odt: 'odt',
  epub: 'epub',
  ppt: 'ppt',
  pptx: 'pptx',
  pptm: 'pptx',
  ppsx: 'pptx',
  odp: 'odp',
  xls: 'xlsx',
  xlsx: 'xlsx',
  xlsm: 'xlsx',
  xlsb: 'xlsx',
  ods: 'ods',
};

/** Every extension the converter will take, for the file picker's `accept` list to be built from. */
export const CONVERTIBLE_EXTENSIONS = Object.keys(CONVERTIBLE);

/** Which format a file name calls for, or `undefined` when the converter isn't the right tool. */
export function convertibleFormat(fileName: string | undefined): string | undefined {
  const name = fileName ?? '';
  const dot = name.lastIndexOf('.');
  return dot < 0 ? undefined : CONVERTIBLE[name.slice(dot + 1).toLowerCase()];
}

/**
 * anydoc's output is GitHub-Flavored Markdown, and `parseEvents` reads plain lines — so a heading
 * arrives as a title of `## Rehearsal` and a bulleted fixture list keeps its bullets. The decoration
 * is stripped here rather than in the parser, which the app feeds plain text from a model.
 *
 * A table becomes one comma-separated line per row, which is what a fixture list in a spreadsheet
 * is, and the separator row goes.
 */
export function flattenMarkdown(markdown: string): string {
  return markdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => !/^\|?[\s:|-]*\|[\s:|-]*\|?$/.test(line) || !line.includes('|'))
    .map((line) =>
      line.startsWith('|')
        ? line
            .replace(/^\||\|$/g, '')
            .split('|')
            .map((cell) => cell.trim())
            .filter((cell) => cell !== '')
            .join(', ')
        : line,
    )
    .map((line) => (/^[-*_]{3,}$/.test(line) ? '' : line))
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '')
        .replace(/^>\s?/, '')
        .replace(/^([-*+]|\d+[.)])\s+/, '')
        .replace(/^\[[ x]\]\s+/i, ''),
    )
    .map((line) => line.replace(/\*\*|__|~~|`/g, '').replace(/\[([^\]]*)\]\([^)]*\)/g, '$1'))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Why there is no text, when there is none — the funnel turns this into something to do next. */
export type ConvertFailure = 'scanned' | 'unreadable';

export interface ConvertRead {
  /** The words in the document, or `null` when there were none to be had. */
  readonly text: string | null;
  readonly failure?: ConvertFailure;
}

export interface SiteConverter {
  read(file: File, format: string, onReady?: () => void): Promise<ConvertRead>;
}

/** The shape anydoc throws for a PDF whose pages are pictures. */
function isNeedsOcr(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'needsOcr';
}

export function createConverter(load: LoadConverter = loadAnydoc): SiteConverter {
  /** Kept between reads: the module costs a download and an instantiation, and files come in pairs. */
  let engine: Promise<Engine> | null = null;

  return {
    async read(file: File, format: string, onReady?: () => void): Promise<ConvertRead> {
      try {
        engine ??= load();
        const converter = await engine;
        onReady?.();
        const bytes = new Uint8Array(await file.arrayBuffer());
        // Conversion is synchronous inside the module, so the timeout can only cover the load and
        // the read around it. That is the part that varies with the network; the conversion itself
        // is milliseconds on anything this site runs on.
        const guard = new Promise<null>((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS));
        const markdown = await Promise.race([
          Promise.resolve().then(() => converter.toMarkdownBytes(bytes, format)),
          guard,
        ]);
        // `null` is the guard firing — the module never came down. An empty conversion is a file
        // with no text in it, which is the same situation as `needsOcr`.
        if (markdown === null) return { text: null, failure: 'unreadable' };
        const text = flattenMarkdown(markdown);
        return text === '' ? { text: null, failure: 'scanned' } : { text };
      } catch (error) {
        if (isNeedsOcr(error)) return { text: null, failure: 'scanned' };
        engine = null; // willing to try again rather than staying broken
        return { text: null, failure: 'unreadable' };
      }
    },
  };
}
